// Third party libs/components
import React, { useEffect, useState } from 'react';
import P from 'prop-types';
import { useSelector } from 'react-redux';
import Style from './style.module.css';
import { useLocation } from '@reach/router';

// Utils
import { translate as t } from '@utils/translate';
import { formatCurrency } from '@utils/currency';
import useRequest from '@utils/useRequest';
import { apiHost } from '@utils/constants';
import { getFormatedCurrencyByCode } from '@utils/search';

const CheckDisponibility = ({ title, description, openQuotation }) => {
  const {
    check__disponibility,
    check__title,
    check__description,
    button,
  } = Style;
  const TEXTS = {
    CHARTER_THIS_AIRCRAFT: t('charterThisAircraft'),
  };
  return (
    <div className={check__disponibility}>
      <div>
        <p className={check__title}>{title}</p>
        <p className={check__description}>{description}</p>
      </div>
      <button onClick={openQuotation} className={button}>
        {TEXTS.CHARTER_THIS_AIRCRAFT}
      </button>
    </div>
  );
};

CheckDisponibility.propTypes = {
  title: P.string,
  description: P.string,
  openQuotation: P.func,
};

const RequestToBuy = ({
  buttonLabel,
  title,
  description,
  openRequestToBuy,
}) => {
  const { check__toBuy, check__title, check__description, button } = Style;

  return (
    <div className={check__toBuy}>
      <div>
        <p className={check__title}>{title}</p>
        <p className={check__description}>{description}</p>
      </div>
      <button
        onClick={openRequestToBuy}
        className={[button, Style.dark].join(' ')}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

RequestToBuy.propTypes = {
  buttonLabel: P.string,
  title: P.string,
  description: P.string,
  openRequestToBuy: P.func,
};

const SelectAircraft = ({ price, currency, quantityOfPassengers }) => {
  const {
    check__disponibility,
    priceQuotation,
    quantityPassengersContent,
    quantityPassengers,
    button,
  } = Style;
  const TEXTS = {
    AVAILABLE_SEATS: t('availableSeats'),
    SELECT_AIRCRAFT: t('selectAircraft'),
  };
  return (
    <div className={check__disponibility}>
      <div className={quantityPassengersContent}>
        <span className={quantityPassengers}>{quantityOfPassengers}</span>{' '}
        <span className={quantityPassengers}>{TEXTS.AVAILABLE_SEATS}</span>
        <p className={priceQuotation}>
          {formatCurrency({
            value: price,
            currency: currency,
          })}
        </p>
      </div>
      <button className={button}>{TEXTS.SELECT_AIRCRAFT}</button>
    </div>
  );
};

SelectAircraft.propTypes = {
  price: P.string,
  currency: P.string,
  quantityOfPassengers: P.number,
};

const BrandInfo = ({ companyName, companySvg, country, foundedAt }) => {
  const {
    company,
    companyInfo,
    companyBrand,
    companyTextCountry,
    companyTextFoundedAt,
    companyImage,
  } = Style;
  const TEXTS = {
    SINCE: t('since'),
  };

  return (
    <span className={company}>
      <img src={companySvg} className={companyImage} />
      <span className={companyInfo}>
        <p className={companyBrand}>{companyName}</p>
        <p className={companyTextFoundedAt}>{`${TEXTS.SINCE} ${foundedAt}`}</p>
        <p className={companyTextCountry}>{country}</p>
      </span>
    </span>
  );
};

BrandInfo.propTypes = {
  companyName: P.string,
  companySvg: P.string,
  country: P.string,
  foundedAt: P.number,
};

const AircraftDescription = ({
  aircraftId,
  ptitle,
  scoreSvg,
  pscore,
  ptext,
  company,
  openQuotation,
  flightPage,
  quotation,
  isSaleable,
  openRequestToBuy,
  manufacturedAt,
  continent,
}) => {
  const {
    description,
    header,
    title,
    starIcon,
    score,
    price,
    companyDesktop,
    wrapper,
    text,
    companyMobile,
    hide,
    actionsWrapper,
  } = Style;

  const TEXTS = {
    GET_IN_TOUCH_BUTTON: t('getInTouch'),
    AIRCRAFT_FOR_SALE_TITLE: t('aircraftForSaleTitle'),
    AIRCRAFT_FOR_SALE_DESC: t('aircraftForSaleDescription'),
    AIRCRAFT_PAGE_IN: t('aircraftPageIn'),
    AIRCRAFT_CHARTER: t('aircraftPageCharter'),
    AVAILABLE_FOR_CHARTER: t('aircraftPageAvailableForCharter'),
    CONTACT_US_FOR_PRICING: t('contactUsForPricingLabel'),
    FRACTIONAL_AVAILABLE: t('fractionalAvailableLabel'),
  };

  const [priceToSell, setPriceToSell] = useState(0);
  const [currency, setCurrency] = useState('USD');
  const [contactUsForPricing, setContactUsForPricing] = useState(0);
  const [isFractional, setIsFractional] = useState(0);
  const { search } = useLocation();

  useEffect(() => {
    (async () => {
      if (search.includes('forSale=1')) {
        const {
          data: { aircraft },
        } = await useRequest({
          url: `${apiHost}/api/v1/aircraft/getaircraftbyid?id=${aircraftId}`,
        });

        setPriceToSell(aircraft.priceToSell);
        setCurrency(aircraft.currencyToSell);
        setContactUsForPricing(aircraft.contactUsForPricing);
        setIsFractional(aircraft.isFractional);
      }
    })();
  }, []);

  return (
    <section
      className={[
        description,
        'container',
        flightPage ? Style.flightPage : '',
      ].join(' ')}
    >
      <div className={header}>
        <h1 className={title}>{`${ptitle} ${
          manufacturedAt ? '(' + manufacturedAt?.split('-')[0] + ')' : ''
        } ${TEXTS.AVAILABLE_FOR_CHARTER} ${
          continent ? TEXTS.AIRCRAFT_PAGE_IN : ''
        } ${continent || ''}`}</h1>

        {search.includes('forSale=1') && Number(isFractional) ? (
          <span className={[score, price].join(' ')}>
            {TEXTS.FRACTIONAL_AVAILABLE}
          </span>
        ) : search.includes('forSale=1') && !Number(contactUsForPricing) ? (
          <span className={[score, price].join(' ')}>
            {getFormatedCurrencyByCode(priceToSell, currency)}
          </span>
        ) : (
          <p className={[score, price].join(' ')}>
            {TEXTS.CONTACT_US_FOR_PRICING}
          </p>
        )}

        <span className={companyDesktop}>
          {company && <BrandInfo {...company} />}
        </span>
      </div>
      <div className={[wrapper, flightPage ? hide : ''].join(' ')}>
        <p className={text}>{ptext}</p>
        <div className={actionsWrapper}>
          {isSaleable && !quotation ? (
            <RequestToBuy
              openRequestToBuy={openRequestToBuy}
              title={TEXTS.AIRCRAFT_FOR_SALE_TITLE}
              description={TEXTS.AIRCRAFT_FOR_SALE_DESC}
              buttonLabel={TEXTS.GET_IN_TOUCH_BUTTON}
            />
          ) : null}
          {!quotation ? (
            <CheckDisponibility openQuotation={openQuotation} title={''} />
          ) : null}
          {quotation ? (
            <SelectAircraft
              price={quotation.price}
              currency={quotation.currency}
              quantityOfPassengers={quotation.aircraft.quantityOfPassengers}
            />
          ) : null}
        </div>
      </div>
      <span className={companyMobile}>
        <BrandInfo {...company} />
      </span>
    </section>
  );
};

AircraftDescription.propTypes = {
  aircraftId: P.number,
  ptitle: P.string,
  scoreSvg: P.string,
  pscore: P.number,
  ptext: P.string,
  company: P.object,
  openQuotation: P.func,
  flightPage: P.bool,
  quotation: P.oneOfType([P.string, P.object]),
  isSaleable: P.number,
  openRequestToBuy: P.func,
  manufacturedAt: P.string,
};

export default AircraftDescription;
